import { call, put, takeLatest } from 'redux-saga/effects'
import fetchFromAPI, { getDefaultAutorizedHeaders } from 'helpers/fetchFromApi'
import * as ApiRoute from 'constants/apiRouting'
import UrlHelper from 'helpers/url'
import NoticeHelper from 'helpers/notice'
import { SORT_DIRECTION } from 'types/common'
import { MANAGEMENT_UI_EVERYTHING } from 'constants/index'
import { SHOW_NOTICE } from '../notice'
import {
  SET_MANAGEMENT_UI_IS_LOADING,
  FETCH_MANAGEMENT_UI_DATA,
  SUCCESS_MANAGEMENT_UI_DATA,
  FAILURE_MANAGEMENT_UI_DATA,
  SUCCESS_MANAGEMENT_UI_FILTERS,
  FAILURE_MANAGEMENT_UI_FILTERS,
  FETCH_MANAGEMENT_UI_FILTERS,
  UPDATE_MANAGEMENT_UI_TASK_ASSIGNEES,
  SUCCESS_MANAGEMENT_UI_TASK_ASSIGNEES,
  SET_MANAGEMENT_UI_IS_UPDATING,
  UPDATE_MANAGEMENT_UI_JOB_ASSIGNEES,
  SUCCESS_MANAGEMENT_UI_JOB_ASSIGNEES,
  DELETE_MANAGEMENT_UI_JOB,
  SUCCESS_DELETE_MANAGEMENT_UI_JOB,
  UPDATE_MANAGEMENT_UI_TASK,
  SUCCESS_MANAGEMENT_UI_TASK,
  DELETE_MANAGEMENT_UI_TASK,
  SUCCESS_DELETE_MANAGEMENT_UI_TASK,
  CREATE_MANAGEMENT_UI_JOB,
  SET_PLAN_NEW_JOB_IS_SAVING,
  RESTORE_JOB_SUCCESS,
  RESTORE_JOB,
  RESTORE_JOB_SERIES_SUCCESS,
  RESTORE_JOB_SERIES,
} from './reducer'

function* fetchManagementUIData({
  payload: {
    tabName,
    query,
    search,
    pagination: { limit, page },
    sort: { field, order },
  },
  promise,
}) {
  try {
    yield put({ type: SET_MANAGEMENT_UI_IS_LOADING, payload: true })

    const sort = `&sort=${order === SORT_DIRECTION.ASC ? '' : '-'}${field}`

    const response = yield call(fetchFromAPI, {
      headers: getDefaultAutorizedHeaders(),
      partUrl: `${ApiRoute.MANAGEMENT_UI}/${
        tabName || MANAGEMENT_UI_EVERYTHING
      }${query}${
        search.length ? `&search=${search}` : ''
      }&limit=${limit}&page=${page}${field ? sort : ''}`,
    })

    if (response && !response.error) {
      yield put({ type: SUCCESS_MANAGEMENT_UI_DATA, payload: response })
    } else {
      yield put({ type: FAILURE_MANAGEMENT_UI_DATA })
    }

    if (promise && promise.resolve) {
      yield call(promise.resolve, response)
    }

    yield put({ type: SET_MANAGEMENT_UI_IS_LOADING, payload: false })
  } catch (error) {
    console.error('FETCH MANAGEMENT UI DATA FAILED:', error)
  }
}

function* fetchManagementUIFilters() {
  try {
    const response = yield call(fetchFromAPI, {
      headers: getDefaultAutorizedHeaders(),
      partUrl: `${ApiRoute.MANAGEMENT_UI_FILTERS}`,
    })

    if (response && !response.error) {
      yield put({ type: SUCCESS_MANAGEMENT_UI_FILTERS, payload: response })
    } else {
      yield put({ type: FAILURE_MANAGEMENT_UI_FILTERS })
    }
  } catch (error) {
    console.error('FETCH MANAGEMENT UI FILTERS LIST FAILED:', error)
  }
}

function* updateTaskAssignees({ payload }) {
  const {
    params,
    additionalParams,
    data,
    successMessage,
    errorMessage,
  } = payload
  const { assigned_users, assigned_user_groups } = data

  try {
    yield put({ type: SET_MANAGEMENT_UI_IS_UPDATING, payload: true })

    const updatedData = {
      assigned_users: assigned_users.map(({ id }) => id),
      assigned_user_groups: assigned_user_groups.map(({ id }) => id),
    }

    const response = yield call(fetchFromAPI, {
      headers: getDefaultAutorizedHeaders(),
      partUrl: UrlHelper.create(ApiRoute.MY_JOBS_UPDATE_TASK_ASSIGNEES, params),
      method: 'PUT',
      data: updatedData,
    })

    if (response && !response.error) {
      yield put({
        type: SUCCESS_MANAGEMENT_UI_TASK_ASSIGNEES,
        payload: {
          data,
          params: additionalParams,
        },
      })
      yield put({
        type: SHOW_NOTICE,
        notice: NoticeHelper.createSuccessObj(successMessage),
      })
    } else {
      yield put({
        type: SHOW_NOTICE,
        notice: NoticeHelper.createErrorObj(errorMessage),
      })
    }

    yield put({ type: SET_MANAGEMENT_UI_IS_UPDATING, payload: false })
  } catch (error) {
    yield put({ type: SET_MANAGEMENT_UI_IS_UPDATING, payload: false })
    console.error('JOB TASK ASSIGNEES UPDATE FAILED ', error)
  }
}

function* updateJobAssignees({ payload }) {
  const {
    params,
    additionalParams,
    data,
    successMessage,
    errorMessage,
  } = payload
  const {
    add: { users: usersToAdd, user_groups: groupsToAdd },
    remove: { users: usersToRemove, user_groups: groupsToRemove },
  } = data

  try {
    yield put({ type: SET_MANAGEMENT_UI_IS_UPDATING, payload: true })

    const updatedData = {
      add: {
        users: usersToAdd.map(({ id }) => id),
        user_groups: groupsToAdd.map(({ id }) => id),
      },
      remove: {
        users: usersToRemove.map(({ id }) => id),
        user_groups: groupsToRemove.map(({ id }) => id),
      },
    }

    const response = yield call(fetchFromAPI, {
      headers: getDefaultAutorizedHeaders(),
      partUrl: UrlHelper.create(ApiRoute.MY_JOBS_UPDATE_JOB, params),
      method: 'PUT',
      data: updatedData,
    })

    if (response && !response.error) {
      yield put({
        type: SUCCESS_MANAGEMENT_UI_JOB_ASSIGNEES,
        payload: {
          data,
          params: additionalParams,
        },
      })
      yield put({
        type: SHOW_NOTICE,
        notice: NoticeHelper.createSuccessObj(successMessage),
      })
    } else {
      yield put({
        type: SHOW_NOTICE,
        notice: NoticeHelper.createErrorObj(errorMessage),
      })
    }

    yield put({ type: SET_MANAGEMENT_UI_IS_UPDATING, payload: false })
  } catch (error) {
    yield put({ type: SET_MANAGEMENT_UI_IS_UPDATING, payload: false })
    console.error('JOB ASSIGNEES UPDATE FAILED ', error)
  }
}

function* deleteJob({ payload }) {
  const {
    id,
    job_series_id,
    series,
    successMessage,
    errorMessage,
    data,
    isSchedulingTab,
  } = payload

  try {
    yield put({ type: SET_MANAGEMENT_UI_IS_UPDATING, payload: true })

    const singleOrSeriesUrl = series ? `${id}${ApiRoute.JOB_SERIES}` : id
    const response = yield call(fetchFromAPI, {
      headers: getDefaultAutorizedHeaders(),
      partUrl: isSchedulingTab
        ? `${ApiRoute.JOB_SERIES}/${id}`
        : `${ApiRoute.MY_JOBS}/${singleOrSeriesUrl}`,
      method: 'DELETE',
      data,
    })

    if (response && !response.error) {
      yield put({
        type: SUCCESS_DELETE_MANAGEMENT_UI_JOB,
        payload: {
          id,
          job_series_id: series ? job_series_id : undefined,
        },
      })
      yield put({
        type: SHOW_NOTICE,
        notice: NoticeHelper.createSuccessObj(successMessage),
      })
    } else {
      yield put({
        type: SHOW_NOTICE,
        notice: NoticeHelper.createErrorObj(errorMessage),
      })
    }

    yield put({ type: SET_MANAGEMENT_UI_IS_UPDATING, payload: false })
  } catch (error) {
    yield put({ type: SET_MANAGEMENT_UI_IS_UPDATING, payload: false })
    console.error('DELETE JOB FAILURE ', error)
  }
}

function* restoreJob({ payload }) {
  const { id, successMessage, errorMessage } = payload

  try {
    yield put({ type: SET_MANAGEMENT_UI_IS_UPDATING, payload: true })

    const response = yield call(fetchFromAPI, {
      headers: getDefaultAutorizedHeaders(),
      partUrl: UrlHelper.create(ApiRoute.MY_JOBS_UPDATE_JOB, {
        id,
        action: 'recover',
      }),
      method: 'PUT',
    })

    if (response && !response.error) {
      yield put({
        type: RESTORE_JOB_SUCCESS,
        payload: {
          id,
        },
      })
      yield put({
        type: SHOW_NOTICE,
        notice: NoticeHelper.createSuccessObj(successMessage),
      })
    } else {
      yield put({
        type: SHOW_NOTICE,
        notice: NoticeHelper.createErrorObj(errorMessage),
      })
    }

    yield put({ type: SET_MANAGEMENT_UI_IS_UPDATING, payload: false })
  } catch (error) {
    yield put({ type: SET_MANAGEMENT_UI_IS_UPDATING, payload: false })
    console.error('RESTORE JOB FAILURE ', error)
  }
}

function* restoreJobSeries({ payload }) {
  const { job_series_id, successMessage, errorMessage } = payload

  try {
    yield put({ type: SET_MANAGEMENT_UI_IS_UPDATING, payload: true })

    const response = yield call(fetchFromAPI, {
      headers: getDefaultAutorizedHeaders(),
      partUrl: UrlHelper.create(ApiRoute.JOB_SERIES_RECOVER, {
        id: job_series_id,
      }),
      method: 'PUT',
    })

    if (response && !response.error) {
      yield put({
        type: RESTORE_JOB_SERIES_SUCCESS,
        payload: {
          job_series_id,
        },
      })
      yield put({
        type: SHOW_NOTICE,
        notice: NoticeHelper.createSuccessObj(successMessage),
      })
    } else {
      yield put({
        type: SHOW_NOTICE,
        notice: NoticeHelper.createErrorObj(errorMessage),
      })
    }

    yield put({ type: SET_MANAGEMENT_UI_IS_UPDATING, payload: false })
  } catch (error) {
    yield put({ type: SET_MANAGEMENT_UI_IS_UPDATING, payload: false })
    console.error('RESTORE JOB SERIES FAILURE ', error)
  }
}

function* updateTask({ payload }) {
  const { task, params, action, successMessage, errorMessage } = payload

  try {
    yield put({ type: SET_MANAGEMENT_UI_IS_UPDATING, payload: true })

    const response = yield call(fetchFromAPI, {
      headers: getDefaultAutorizedHeaders(),
      partUrl: UrlHelper.create(ApiRoute.MY_JOBS_UPDATE_TASK, {
        id: task.id,
        action,
      }),
      method: 'PUT',
    })

    if (response && !response.error) {
      yield put({
        type: SUCCESS_MANAGEMENT_UI_TASK,
        payload: {
          newTaskId: response.id,
          task,
          params,
        },
      })
      yield put({
        type: SHOW_NOTICE,
        notice: NoticeHelper.createSuccessObj(successMessage),
      })
    } else {
      yield put({
        type: SHOW_NOTICE,
        notice: NoticeHelper.createErrorObj(errorMessage),
      })
    }

    yield put({ type: SET_MANAGEMENT_UI_IS_UPDATING, payload: false })
  } catch (error) {
    yield put({ type: SET_MANAGEMENT_UI_IS_UPDATING, payload: false })
    console.error('UPDATE TASK FAILURE ', error)
  }
}

function* deleteTask({ payload }) {
  const { id, successMessage, errorMessage, params } = payload

  try {
    yield put({ type: SET_MANAGEMENT_UI_IS_UPDATING, payload: true })

    const response = yield call(fetchFromAPI, {
      headers: getDefaultAutorizedHeaders(),
      partUrl: `${ApiRoute.MY_JOB_TASKS}/${id}`,
      method: 'DELETE',
    })

    if (response && !response.error) {
      yield put({
        type: SUCCESS_DELETE_MANAGEMENT_UI_TASK,
        payload: {
          id,
          params,
        },
      })
      yield put({
        type: SHOW_NOTICE,
        notice: NoticeHelper.createSuccessObj(successMessage),
      })
    } else {
      yield put({
        type: SHOW_NOTICE,
        notice: NoticeHelper.createErrorObj(errorMessage),
      })
    }

    yield put({ type: SET_MANAGEMENT_UI_IS_UPDATING, payload: false })
  } catch (error) {
    yield put({ type: SET_MANAGEMENT_UI_IS_UPDATING, payload: false })
    console.error('DELETE JOB FAILURE ', error)
  }
}

function* createJob({
  payload: { form, errorMessage, successMessage },
  promise,
}) {
  try {
    yield put({ type: SET_PLAN_NEW_JOB_IS_SAVING, payload: true })

    const response = yield call(fetchFromAPI, {
      headers: getDefaultAutorizedHeaders(),
      partUrl: ApiRoute.MANAGEMENT_UI,
      method: 'POST',
      data: form,
    })

    if (response && !Object.prototype.hasOwnProperty.call(response, 'error')) {
      yield put({
        type: SHOW_NOTICE,
        notice: NoticeHelper.createSuccessObj(successMessage),
      })
    } else {
      const errorsArr = Object.values(response.errors) || {}

      for (const i of errorsArr.keys()) {
        yield put({
          type: SHOW_NOTICE,
          notice: NoticeHelper.createErrorObj(errorsArr[i] || errorMessage),
        })
      }
    }

    if (promise && promise.resolve) {
      yield call(promise.resolve, response)
    }
    yield put({ type: SET_PLAN_NEW_JOB_IS_SAVING, payload: false })
  } catch (error) {
    yield put({ type: SET_PLAN_NEW_JOB_IS_SAVING, payload: false })
    console.error('CREATE JOB FAILURE ', error)
  }
}

function* managementUISaga() {
  yield takeLatest(FETCH_MANAGEMENT_UI_DATA, fetchManagementUIData)
  yield takeLatest(FETCH_MANAGEMENT_UI_FILTERS, fetchManagementUIFilters)
  yield takeLatest(UPDATE_MANAGEMENT_UI_TASK_ASSIGNEES, updateTaskAssignees)
  yield takeLatest(UPDATE_MANAGEMENT_UI_JOB_ASSIGNEES, updateJobAssignees)
  yield takeLatest(DELETE_MANAGEMENT_UI_JOB, deleteJob)
  yield takeLatest(UPDATE_MANAGEMENT_UI_TASK, updateTask)
  yield takeLatest(DELETE_MANAGEMENT_UI_TASK, deleteTask)
  yield takeLatest(CREATE_MANAGEMENT_UI_JOB, createJob)
  yield takeLatest(RESTORE_JOB, restoreJob)
  yield takeLatest(RESTORE_JOB_SERIES, restoreJobSeries)
}

export default managementUISaga
